import type { JSX } from "react";

import { getFilteredCountryCodes, PhoneInput, sanitizeCountryCode } from "@expert/common-ui";
import { Box, Group, Select, Stack } from "@mantine/core";
import { type CountryCode, isValidPhoneNumber } from "libphonenumber-js";
import { useMemo, useState } from "react";

import type { Contact } from "@/shared-types";

import { groupContacts, tryGetByVdnName } from "@/shared-utils/transfer";

import { useContactsStateStore } from "../../vdnContacts.store";
import { NameInput } from "../NameInput/NameInput";
import { VdnInput } from "../VdnInput/VdnInput";
import classes from "./SegmentedTransferInput.module.css";

const INPUT_MODES = {
    phoneNumber: "Phone Number",
    vdnExtension: "VDN Extension",
    name: "Name",
} as const;
type InputMode = (typeof INPUT_MODES)[keyof typeof INPUT_MODES];
const INPUT_MODE_LIST = [INPUT_MODES.phoneNumber, INPUT_MODES.vdnExtension, INPUT_MODES.name] as const;
const DEFAULT_MODES = [INPUT_MODES.phoneNumber, INPUT_MODES.name] as const;
export interface SegmentedTransferInputProps {
    setTransferTfn: (tfn: string | undefined) => void;
    setTransferVdn: (vdn: Contact | undefined) => void;
    countrySelectEnabled: boolean;
    clientEnabledCountries: CountryCode[];
    isVdnTransferInputEnabled: boolean;
    defaultCountry: CountryCode;
}

export function SegmentedTransferInput({
    setTransferTfn,
    setTransferVdn,
    countrySelectEnabled,
    clientEnabledCountries,
    isVdnTransferInputEnabled,
    defaultCountry,
}: SegmentedTransferInputProps): JSX.Element {
    /*
     * Controls which component renders. Also points isValidDestination and
     * setDestination to the correct input value.
     */
    const [mode, setMode] = useState<InputMode>("Phone Number");

    /**
     * Holds the value for each mode.
     */
    const [phoneNumber, setPhoneNumber] = useState("");
    const [vdn, setVdn] = useState("");
    const [name, setName] = useState("");

    /*
     * Contacts are fetched from multiple sources and grouped to
     * be used in the NameInput Autocomplete component.
     */
    const { contacts } = useContactsStateStore();
    const groupedContacts = useMemo(() => groupContacts(contacts), [contacts]);

    const normalizedCountryList = getFilteredCountryCodes(clientEnabledCountries);
    const normalizedDefaultCountry: CountryCode = sanitizeCountryCode(defaultCountry);

    // Default modes include Phone and Name. We provide all modes only if VDN input is enabled.
    const availableInputModes = isVdnTransferInputEnabled ? INPUT_MODE_LIST : DEFAULT_MODES;

    /*
     * Narrows Combobox string | null value to InputMode type.
     */
    function isInputMode(value: string | null): value is InputMode {
        return value != null && (INPUT_MODE_LIST as readonly string[]).includes(value);
    }

    /**
     * Points to an input state based on the mode.
     */
    const inputByMode = (mode: InputMode): string => {
        switch (mode) {
            case INPUT_MODES.phoneNumber:
                return phoneNumber;
            case INPUT_MODES.vdnExtension:
                return vdn;
            case INPUT_MODES.name:
                return name;
        }
    };

    /*
     * TFN and VDN are an exclusive or (XOR) relationship.
     * If one is set, the other should be cleared.
     * The addParticipant API expects either a valid tfn or extension,
     * but never both at the same time.
     */
    const updateTransferDestination = (mode: InputMode, input: string) => {
        switch (mode) {
            case INPUT_MODES.phoneNumber:
                setTransferTfn(isValidPhoneNumber(input) ? input : undefined);
                setTransferVdn(undefined);
                break;
            case INPUT_MODES.vdnExtension:
                setTransferTfn(undefined);
                setTransferVdn(contacts.find((c) => c.vdn === input));
                break;
            case INPUT_MODES.name:
                setTransferTfn(undefined);
                setTransferVdn(tryGetByVdnName(input, contacts));
                break;
        }
    };

    const handleModeChange = (mode: string | null) => {
        if (!isInputMode(mode)) {
            return;
        }
        setMode(mode);
        updateTransferDestination(mode, inputByMode(mode));
    };

    const handlePhoneInputChange = (value: string) => {
        setPhoneNumber(value);
        updateTransferDestination(INPUT_MODES.phoneNumber, value);
    };

    const handleVdnInputChange = (value: string) => {
        setVdn(value);
        updateTransferDestination(INPUT_MODES.vdnExtension, value);
    };

    const handleNameInputChange = (value: string) => {
        setName(value);
        updateTransferDestination(INPUT_MODES.name, value);
    };

    return (
        <Stack>
            <Box>
                <Group className={classes.group}>
                    <Select
                        value={mode}
                        data={availableInputModes}
                        onChange={handleModeChange}
                        allowDeselect={false}
                        classNames={{
                            input: classes.input,
                            root: classes.selectRoot,
                        }}
                        data-testid="input-type-select"
                        styles={{
                            input: { backgroundColor: "var(--mantine-color-dark-8)" },
                            label: {
                                fontWeight: "var(--mantine-font-weight-heavy)",
                                fontSize: "var(--mantine-font-size-sm)",
                            },
                        }}
                        label="Transfer the customer"
                    />
                    <Box className={classes.inputBox}>
                        {mode === "Phone Number" && (
                            <PhoneInput
                                value={phoneNumber}
                                onChange={handlePhoneInputChange}
                                countrySelectEnabled={countrySelectEnabled}
                                clientEnabledCountries={normalizedCountryList}
                                defaultCountry={normalizedDefaultCountry}
                            />
                        )}
                        {mode === "VDN Extension" && <VdnInput value={vdn} onChange={handleVdnInputChange} />}
                        {mode === "Name" && (
                            <NameInput value={name} onChange={handleNameInputChange} contacts={groupedContacts} />
                        )}
                    </Box>
                </Group>
            </Box>
        </Stack>
    );
}
